import { useCallback, useState, useEffect, useRef } from 'react';

export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const swRegistration = useRef<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Update permission state when it changes
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    
    // Register service worker for push notifications
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('✅ Service Worker registered successfully:', registration.scope);
          swRegistration.current = registration;
        })
        .catch((error) => {
          console.error('❌ Service Worker registration failed:', error);
        });
    } else {
      console.log('⚠️ Service Worker not supported in this browser');
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    // If already granted, return true
    if (Notification.permission === 'granted') {
      setPermission('granted');
      return true;
    }

    // If denied, we can't ask again
    if (Notification.permission === 'denied') {
      console.log('Notifications are denied by user');
      return false;
    }

    // Request permission
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  const sendNotification = useCallback((title: string, options?: NotificationOptions) => {
    console.log('📢 sendNotification called with:', title, options);
    
    if (!('Notification' in window)) {
      console.log('❌ Notifications not supported in this browser');
      return null;
    }
    
    console.log('Current permission:', Notification.permission);
    
    if (Notification.permission !== 'granted') {
      console.log('❌ Notification permission not granted:', Notification.permission);
      return null;
    }

    try {
      // Always use basic Notification API for reliability on desktop
      console.log('🖥️ Creating notification:', title, options?.body);
      
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        requireInteraction: false,
        silent: false,
        ...options,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto close after 8 seconds
      setTimeout(() => notification.close(), 8000);
      console.log('✅ Notification created successfully');
      return notification;
    } catch (error) {
      console.error('❌ Error creating notification:', error);
      
      // Fallback to Service Worker for mobile
      if (swRegistration.current) {
        try {
          console.log('📱 Fallback to Service Worker');
          swRegistration.current.showNotification(title, {
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            requireInteraction: false,
            ...options,
          });
          console.log('✅ SW Notification sent');
        } catch (swError) {
          console.error('❌ SW notification error:', swError);
        }
      }
      return null;
    }
  }, []);

  const notifyNewMessage = useCallback((senderName: string, messagePreview?: string) => {
    // Use unique tag with timestamp to allow multiple notifications
    return sendNotification(senderName, {
      body: messagePreview || 'Новое сообщение',
      tag: `chat-message-${Date.now()}`,
    });
  }, [sendNotification]);

  const notifyNewTask = useCallback((taskTitle: string, fromUser?: string) => {
    return sendNotification('Новая задача', {
      body: fromUser ? `${fromUser}: ${taskTitle}` : taskTitle,
      tag: `task-${Date.now()}`,
    });
  }, [sendNotification]);

  const notifyTaskUpdate = useCallback((taskTitle: string, status?: string) => {
    return sendNotification('Обновление задачи', {
      body: status ? `${taskTitle} - ${status}` : taskTitle,
      tag: `task-update-${Date.now()}`,
    });
  }, [sendNotification]);

  const notifySupportResponse = useCallback((ticketTitle: string) => {
    return sendNotification('Ответ от поддержки', {
      body: ticketTitle,
      tag: `support-${Date.now()}`,
    });
  }, [sendNotification]);

  return {
    requestPermission,
    sendNotification,
    notifyNewMessage,
    notifyNewTask,
    notifyTaskUpdate,
    notifySupportResponse,
    isSupported: 'Notification' in window,
    permission,
  };
}
